import React from 'react';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import arrowUpSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-up-small';
import arrowDownSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-down-small';
import { AssociateTrend } from './constants';
import { AssociateImageQuality, QualityThresholds } from './model';
import { formatPct, passRatePct, qualityTier, repeatBelowFloorLabel } from './imageQualityUtils';
import { Chip, PassRateBar, textColorForTier, useIqColors } from './meridianUi';

/**
 * Associates worst first, with their top failure, repeat-below-floor flag and trend. Clicking a
 * login opens that associate's photo drill-down.
 */
export interface AssociateListProps {
  associates: AssociateImageQuality[];
  thresholds: QualityThresholds;
  onSelect: (login: string) => void;
  emptyText: string;
}

const TrendArrow: React.FC<{ trend?: AssociateTrend }> = ({ trend }) => {
  if (trend === AssociateTrend.IMPROVING) {
    return (
      <Text tag="span" color="success" data-testid="iq-trend-up">
        <Icon tokens={arrowUpSmallTokens}>Improving</Icon>
      </Text>
    );
  }
  if (trend === AssociateTrend.DECLINING) {
    return (
      <Text tag="span" color="error" data-testid="iq-trend-down">
        <Icon tokens={arrowDownSmallTokens}>Declining</Icon>
      </Text>
    );
  }
  return null;
};

export const LoginLink: React.FC<{ login: string; onSelect: (login: string) => void }> = ({
  login,
  onSelect,
}) => (
  <Link onClick={() => onSelect(login)}>
    <Text tag="span" type="b400" fontFamily="amazonEmberMonospace">
      {login}
    </Text>
  </Link>
);

const AssociateList: React.FC<AssociateListProps> = ({
  associates,
  thresholds,
  onSelect,
  emptyText,
}) => {
  const colors = useIqColors();
  if (associates.length === 0) {
    return (
      <Box spacingInset="500">
        <Text color="secondary">{emptyText}</Text>
      </Box>
    );
  }
  return (
    <Table showDividers spacing="medium" data-testid="iq-associates">
      {associates.map((associate, rowIndex) => {
        const pct = passRatePct(associate.passingPhotos, associate.totalPhotos);
        const tier = qualityTier(pct, thresholds);
        const repeat = repeatBelowFloorLabel(associate, thresholds);
        return (
          <TableRow
            key={associate.login}
            highlightOnHover
            data-testid={`iq-associate-${associate.login}`}
          >
            <TableCell width={56}>
              <Text color="secondary">{rowIndex + 1}</Text>
            </TableCell>
            <TableCell>
              <LoginLink login={associate.login} onSelect={onSelect} />
            </TableCell>
            <TableCell width={190}>
              <PassRateBar
                pct={pct}
                thresholds={thresholds}
                label={`${associate.login} image quality`}
              />
            </TableCell>
            <TableCell width={80} alignmentHorizontal="end">
              <Text type="h300" color={textColorForTier(tier)}>
                {formatPct(pct)}
              </Text>
            </TableCell>
            <TableCell width={120}>
              {associate.topFailure && (
                <Chip dotColor={colors.failure(associate.topFailure.checkId)}>
                  {`${associate.topFailure.failureLabel} · ${associate.topFailure.failedPhotos}`}
                </Chip>
              )}
            </TableCell>
            <TableCell width={210}>{repeat && <Chip muted>{repeat}</Chip>}</TableCell>
            <TableCell width={32}>
              <TrendArrow trend={associate.trend} />
            </TableCell>
            <TableCell width={110} alignmentHorizontal="end">
              <Text color="secondary">{`${associate.totalPhotos} photos`}</Text>
            </TableCell>
          </TableRow>
        );
      })}
    </Table>
  );
};

export default AssociateList;
